import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The tile that asks a shopper who found nothing to write in, sized to borrow a
 * cell of a product grid.
 *
 *   <ContactTile>
 *     <ContactTileGlyph render={<IconQuestionMark />} />
 *     <ContactTileTitle render={<Heading level={3} hover={false} />}>
 *       Nenašli jste, co hledáte?
 *     </ContactTileTitle>
 *     <ContactUsForm />
 *   </ContactTile>
 *
 * A section and not a product card, which is the distinction the card's own
 * rung drew: it takes a card's place in the grid and nothing else about it is a
 * card — no price, no availability, no product.
 *
 * What crosses is the chrome: the cell's own container context, the padding
 * that steps with it, the watermark's frame and the title's type. The form is
 * the tenant's whole — its fields, its validation, its submission and its copy —
 * and composes in as children.
 */

/**
 * The container-query context the tile steps against, and the cell it fills.
 *
 * Rendered by the part rather than left to a composition: every `@prodcard-minwidth`
 * step below resolves against the nearest container, and a composition that
 * omitted this would still render the tile — at its narrow appearance, in a
 * grid cell of any width.
 */
const contactTileFrameClasses = '@container h-full max-h-full w-full bg-background';

/** The cell's contents: the title at the top, the form at the bottom. */
const contactTileClasses = clsxm(
  'relative flex h-full max-h-full flex-col justify-between px-4 py-2',
  '@prodcard-minwidth:px-5 @prodcard-minwidth:py-6'
);

export type ContactTileProps = ComponentProps<'div'>;

/** The tile. */
export const ContactTile = ({ className, ...props }: ContactTileProps) => (
  <div data-slot="contact-tile-frame" className={contactTileFrameClasses}>
    <div data-slot="contact-tile" className={clsxm(contactTileClasses, className)} {...props} />
  </div>
);

/**
 * The watermark behind the tile's contents: hung in the corner, out of the flow
 * and under everything, growing with the cell.
 *
 * Which mark it is stays the tenant's — a question mark here, a brand's own
 * glyph elsewhere — so the artwork arrives through `render` and this part
 * contributes only where it sits and how big it is.
 */
const contactTileGlyphClasses = clsxm(
  'absolute top-0 left-0 z-0 h-[120px] w-[106px]',
  '@prodcard-minwidth:h-[163px] @prodcard-minwidth:w-[142px]'
);

export type ContactTileGlyphProps = ComponentProps<'div'> & {
  /** The artwork to paint. Required: this part has no mark of its own. */
  readonly render: RenderElement;
};

/** The tile's watermark. */
export const ContactTileGlyph = ({ className, render, ...props }: ContactTileGlyphProps) => (
  <RenderedPart
    dataSlot="contact-tile-glyph"
    className={clsxm(contactTileGlyphClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * The tile's opening line. Clamped to two lines and cleared of the watermark,
 * which is why the top padding is this large.
 *
 * Renders a neutral element: a tile repeated in a product grid contributes no
 * heading to the page's outline unless the page says so, which it does by
 * handing one in through `render`.
 */
const contactTileTitleClasses = clsxm(
  'line-clamp-2 pt-32 pr-8 font-medium tracking-tight',
  '@prodcard-minwidth:pt-40 @prodcard-minwidth:pr-0'
);

export type ContactTileTitleProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral `<div>`. */
  readonly render?: RenderElement;
};

/** The tile's title. */
export const ContactTileTitle = ({ className, render, ...props }: ContactTileTitleProps) => (
  <RenderedPart
    dataSlot="contact-tile-title"
    className={clsxm(contactTileTitleClasses, className)}
    render={render}
    {...props}
  />
);
